/**
 * offlineDB.js — IndexedDB wrapper for offline form entries.
 * Stores pending records and allows marking them synced/failed.
 */
const DB_NAME = 'lsn-offline-db';
const DB_VERSION = 1;
const STORE = 'offline_queue';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'offlineId' });
        store.createIndex('status', 'status');
        store.createIndex('module', 'module');
        store.createIndex('created_at', 'created_at');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode) {
  const t = db.transaction(STORE, mode);
  return { store: t.objectStore(STORE), t };
}

export const offlineDB = {
  /**
   * Add a pending entry.
   * @param {Object} entry - { module, displayName, endpoint, method, data }
   */
  async add(entry) {
    const db = await openDB();
    const item = {
      offlineId: `offline-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`,
      status: 'pending',
      created_at: new Date().toISOString(),
      retry_count: 0,
      ...entry,
    };
    return new Promise((resolve, reject) => {
      const { store, t } = tx(db, 'readwrite');
      store.add(item);
      t.oncomplete = () => resolve(item);
      t.onerror = () => reject(t.error);
    });
  },

  /** Get all pending entries in insertion order. */
  async getPending() {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const { store } = tx(db, 'readonly');
      const req = store.index('status').getAll('pending');
      req.onsuccess = () =>
        resolve([...req.result].sort((a, b) => a.created_at.localeCompare(b.created_at)));
      req.onerror = () => reject(req.error);
    });
  },

  /** Count pending entries. */
  async pendingCount() {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const { store } = tx(db, 'readonly');
      const req = store.index('status').count('pending');
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  },

  /** Get all entries (for the sync log view). */
  async getAll(limit = 50) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const { store } = tx(db, 'readonly');
      const req = store.getAll();
      req.onsuccess = () =>
        resolve(
          [...req.result]
            .sort((a, b) => b.created_at.localeCompare(a.created_at))
            .slice(0, limit)
        );
      req.onerror = () => reject(req.error);
    });
  },

  /** Mark an entry as synced. */
  async markSynced(offlineId, serverData) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const { store, t } = tx(db, 'readwrite');
      const get = store.get(offlineId);
      get.onsuccess = () => {
        const item = get.result;
        if (item) {
          item.status = 'synced';
          item.synced_at = new Date().toISOString();
          item.server_data = serverData;
          store.put(item);
        }
        t.oncomplete = resolve;
        t.onerror = () => reject(t.error);
      };
    });
  },

  /** Mark an entry as failed (will retry). */
  async markFailed(offlineId, error) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const { store, t } = tx(db, 'readwrite');
      const get = store.get(offlineId);
      get.onsuccess = () => {
        const item = get.result;
        if (item) {
          item.retry_count = (item.retry_count || 0) + 1;
          // After 3 retries give up
          item.status = item.retry_count >= 3 ? 'failed' : 'pending';
          item.last_error = error;
          item.failed_at = new Date().toISOString();
          store.put(item);
        }
        t.oncomplete = resolve;
        t.onerror = () => reject(t.error);
      };
    });
  },

  /** Delete a single entry. */
  async delete(offlineId) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const { store, t } = tx(db, 'readwrite');
      store.delete(offlineId);
      t.oncomplete = resolve;
      t.onerror = () => reject(t.error);
    });
  },

  /** Clear all synced entries older than 24h. */
  async cleanup() {
    const db = await openDB();
    const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const all = await this.getAll(1000);
    for (const item of all) {
      if (item.status === 'synced' && item.synced_at < cutoff) {
        await this.delete(item.offlineId);
      }
    }
  },
};

export default offlineDB;
